export const PROMOTION_WORKFLOW = {
  IDEA: "idea",
  DRAFT: "draft",
  APPROVED: "approved",
  SCHEDULED: "scheduled",
  PUBLISHED: "published",
  FAILED: "failed",
};

export const getPromotionWorkflowState = (post) => {
  if (!post) return PROMOTION_WORKFLOW.IDEA;
  if (post.publishStatus === "failed") return PROMOTION_WORKFLOW.FAILED;
  if (["scheduled", "posting"].includes(post.publishStatus)) {
    return PROMOTION_WORKFLOW.SCHEDULED;
  }
  if (["posted", "published"].includes(post.publishStatus) || post.queueStatus === "published") {
    return PROMOTION_WORKFLOW.PUBLISHED;
  }
  if (post.queueStatus === "approved") return PROMOTION_WORKFLOW.APPROVED;
  return PROMOTION_WORKFLOW.DRAFT;
};

// Scheduling belongs to one saved platform/request record, never its Idea.
export const canSchedulePromotionPost = (post) => Boolean(post) &&
  !["posted", "published", "posting"].includes(post.publishStatus) &&
  ["draft", "needs_review", "approved"].includes(post.queueStatus || "draft") &&
  ["", "not_ready", "scheduled", "failed"].includes(post.publishStatus || "");

export const PROMOTION_WORKFLOW_LABELS = {
  [PROMOTION_WORKFLOW.IDEA]: "Not created",
  [PROMOTION_WORKFLOW.DRAFT]: "Draft",
  [PROMOTION_WORKFLOW.APPROVED]: "Approved",
  [PROMOTION_WORKFLOW.SCHEDULED]: "Scheduled",
  [PROMOTION_WORKFLOW.PUBLISHED]: "Published",
  [PROMOTION_WORKFLOW.FAILED]: "Failed",
};

export const PROMOTION_WORKFLOW_BADGE_CLASSES = {
  [PROMOTION_WORKFLOW.IDEA]: "text-bg-secondary",
  [PROMOTION_WORKFLOW.DRAFT]: "text-bg-info",
  [PROMOTION_WORKFLOW.APPROVED]: "text-bg-success",
  [PROMOTION_WORKFLOW.SCHEDULED]: "text-bg-primary",
  [PROMOTION_WORKFLOW.PUBLISHED]: "text-bg-dark",
  [PROMOTION_WORKFLOW.FAILED]: "text-bg-danger",
};

const formatBadgeDate = (value, includeTime = false) => {
  if (!value) return "";
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : includeTime ? date.toLocaleString() : date.toLocaleDateString();
};

export const getPromotionWorkflowLabel = (post) => {
  const state = getPromotionWorkflowState(post);
  const date = state === PROMOTION_WORKFLOW.SCHEDULED
    ? formatBadgeDate(post?.scheduledAt)
    : state === PROMOTION_WORKFLOW.PUBLISHED
      ? formatBadgeDate(post?.publishedAt || post?.postedAt)
      : "";

  return date ? `${PROMOTION_WORKFLOW_LABELS[state]} ${date}` : PROMOTION_WORKFLOW_LABELS[state];
};

// History keeps actual publication time distinct from a planned schedule.
export const getPromotionHistoryLabel = (post) => {
  const state = getPromotionWorkflowState(post);
  if (state === PROMOTION_WORKFLOW.PUBLISHED) {
    const published = formatBadgeDate(post?.publishedAt, true) || formatBadgeDate(post?.postedAt, true);
    if (published) return published;
    const scheduled = formatBadgeDate(post?.scheduledAt, true);
    return scheduled ? `Published — scheduled for ${scheduled}` : "Published (date unavailable)";
  }
  if (state === PROMOTION_WORKFLOW.SCHEDULED) {
    const scheduled = formatBadgeDate(post?.scheduledAt, true);
    return scheduled ? `Scheduled for ${scheduled}` : "Scheduled";
  }
  if (state === PROMOTION_WORKFLOW.DRAFT) {
    return ({ needs_review: "Needs Review", rejected: "Rejected", archived: "Archived" })[post?.queueStatus] || "Draft";
  }
  return PROMOTION_WORKFLOW_LABELS[state];
};

// A schedule is not publication evidence. Use only a confirmed record and a
// safe provider URL, or the provider's stable identifier when its URL is known.
export const getPromotionSocialPostUrl = (post) => {
  if (getPromotionWorkflowState(post) !== PROMOTION_WORKFLOW.PUBLISHED) return "";
  const platform = post.platform;
  const hosts = {
    facebook: ["facebook.com", "fb.com"], instagram: ["instagram.com"],
    linkedin: ["linkedin.com"], tiktok: ["tiktok.com"],
  }[platform];
  if (!hosts) return "";
  const results = Array.isArray(post.providerResults) ? [...post.providerResults].reverse() : [];
  const candidates = [post.permalink, post.permalinkUrl, post.postUrl, post.providerPostUrl,
    ...results.flatMap((result) => [result?.permalink, result?.permalinkUrl, result?.postUrl, result?.url])];
  for (const candidate of candidates) {
    try {
      const url = new URL(candidate);
      if (url.protocol === "https:" && !url.username && !url.password &&
          hosts.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`))) return url.href;
    } catch { /* Missing/legacy provider URL: try the next result or stable ID. */ }
  }
  const id = String(post.providerPostId || results.find((result) => result?.providerPostId || result?.externalPostId)?.providerPostId || results.find((result) => result?.externalPostId)?.externalPostId || "");
  if (platform === "facebook" && /^\d+_\d+$/.test(id)) {
    const [page, item] = id.split("_");
    return `https://www.facebook.com/${page}/posts/${item}`;
  }
  if (platform === "linkedin" && /^urn:li:(share|ugcPost):\d+$/.test(id)) return `https://www.linkedin.com/feed/update/${encodeURIComponent(id)}/`;
  return "";
};
